import { ApplicationEventType } from "gen/clients";
import { SelectOption } from "@mui/base";

/**
 * Converts an ApplicationEventType enum value to a human-readable string.
 * @param type - The ApplicationEventType enum value
 * @returns A human-readable description of the event type
 */
export function describeApplicationEventType(type: ApplicationEventType): string {
  switch (type) {
    case ApplicationEventType.APPLICATION_MT_TRIAL_SIGNED_UP:
      return "MT Trial Signed Up";
    case ApplicationEventType.APPLICATION_MT_TRIAL_STOPPED:
      return "MT Trial Stopped";
    case ApplicationEventType.APPLICATION_MT_SUBSCRIPTION:
      return "MT Subscription";
    case ApplicationEventType.APPLICATION_MT_PERWORD:
      return "MT Per-Word";
    case ApplicationEventType.APPLICATION_MT_ENABLED:
      return "MT Enabled";
    case ApplicationEventType.APPLICATION_MT_DISABLED:
      return "MT Disabled";
    case ApplicationEventType.MT_DOC_ESCALATION_TO_HUMAN:
      return "MT Doc Escalation to Human";
    case ApplicationEventType.MT_MEDIA_ESCALATION_TO_HUMAN:
      return "MT Media Escalation to Human";
    case ApplicationEventType.MT_DOC_TRANSLATION_REQUESTED:
      return "MT Doc Translation Requested";
    case ApplicationEventType.MT_MEDIA_TRANSLATION_REQUESTED:
      return "MT Media Translation Requested";
    case ApplicationEventType.AI_POST_EDIT_REQUESTED:
      return "AI Post-Edit Requested";
    case ApplicationEventType.AI_POST_EDIT_ESCALATION_TO_HUMAN:
      return "AI Post-Edit Escalation to Human";
    case ApplicationEventType.NEW_CLIENT_CONTACT_CREATED:
      return "New Client Contact Created";
    case ApplicationEventType.AUTO_QUOTE_ACCEPTED:
      return "Auto-Quote Accepted";
    case ApplicationEventType.AUTO_QUOTE_REJECTED:
      return "Auto-Quote Rejected";
    case ApplicationEventType.AUTO_QUOTE_FAILED:
      return "Auto-Quote Failed";
    case ApplicationEventType.QUOTE_REQUESTED:
      return "Quote Requested";
    case ApplicationEventType.TRANSLATION_REQUESTED:
      return "Translation Requested";
    case ApplicationEventType.USER_LOGGED_IN:
      return "User Logged In";
    case ApplicationEventType.JOB_REQUEST_COMPLETED:
      return "Job Request Completed";
    case ApplicationEventType.PROJECT_CANCELLATION_REQUESTED:
      return "Project Cancellation Requested";
    default:
      return type;
  }
}

/**
 * All available application event type options as SelectOption array.
 * Includes all event types from the ApplicationEventType enum, sorted alphabetically by label.
 */
export const allEventTypeOptions: SelectOption<string>[] = Object.values(ApplicationEventType)
  .map(type => ({
    label: describeApplicationEventType(type),
    value: type
  }))
  .sort((o1, o2) => o1.label.localeCompare(o2.label));

/**
 * Subset of event type options commonly used.
 * Excludes trial and subscription management events.
 */
export const supportedEventTypeOptions: SelectOption<string>[] = [
  ApplicationEventType.MT_DOC_TRANSLATION_REQUESTED,
  ApplicationEventType.MT_MEDIA_TRANSLATION_REQUESTED,
  ApplicationEventType.MT_DOC_ESCALATION_TO_HUMAN,
  ApplicationEventType.MT_MEDIA_ESCALATION_TO_HUMAN,
  ApplicationEventType.NEW_CLIENT_CONTACT_CREATED,
  ApplicationEventType.AUTO_QUOTE_ACCEPTED,
  ApplicationEventType.AUTO_QUOTE_REJECTED,
  ApplicationEventType.AUTO_QUOTE_FAILED,
  ApplicationEventType.QUOTE_REQUESTED,
  ApplicationEventType.TRANSLATION_REQUESTED,
  ApplicationEventType.USER_LOGGED_IN,
  ApplicationEventType.JOB_REQUEST_COMPLETED,
  ApplicationEventType.PROJECT_CANCELLATION_REQUESTED
]
  .map(type => ({
    label: describeApplicationEventType(type),
    value: type
  }))
  .sort((o1, o2) => o1.label.localeCompare(o2.label));
